import React, { useState, useEffect, useCallback } from 'react';
import {
  Target,
  Plus,
  Edit2,
  Trash2,
  Users,
  Calendar,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import fieldTeamApi from '../../services/fieldTeamApi';
import './Targets.css';

// Period type options
const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

function Targets() {
  const [targets, setTargets] = useState([]);
  const [members, setMembers] = useState([]);
  const [teamOverview, setTeamOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });

  // Filters
  const [periodFilter, setPeriodFilter] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // list, team

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    member_id: '',
    period_type: 'weekly',
    period_start: '',
    period_end: '',
    target_meetings: 0,
    target_new_clients: 0,
    target_trials: 0,
    target_sales: 0
  });

  // Bulk form state
  const [bulkData, setBulkData] = useState({
    member_ids: [],
    period_type: 'weekly',
    period_start: '',
    period_end: '',
    target_meetings: 0,
    target_new_clients: 0,
    target_trials: 0,
    target_sales: 0
  });

  // Fetch targets
  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        period_type: periodFilter !== 'all' ? periodFilter : undefined,
        active_only: activeOnly ? 1 : undefined
      };

      const response = await fieldTeamApi.getTargets(params);
      if (response.data.success) {
        setTargets(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Failed to fetch targets:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, periodFilter, activeOnly]);

  // Fetch members
  const fetchMembers = useCallback(async () => {
    try {
      const response = await fieldTeamApi.getMembers();
      if (response.data.success) {
        setMembers(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, []);

  // Fetch team overview
  const fetchTeamOverview = useCallback(async () => {
    try {
      const response = await fieldTeamApi.getTeamTargetOverview(periodFilter !== 'all' ? periodFilter : 'weekly');
      if (response.data.success) {
        setTeamOverview(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch team overview:', error);
    }
  }, [periodFilter]);

  useEffect(() => {
    fetchTargets();
    fetchMembers();
  }, [fetchTargets, fetchMembers]);

  useEffect(() => {
    if (viewMode === 'team') {
      fetchTeamOverview();
    }
  }, [viewMode, fetchTeamOverview]);

  // Calculate default period dates
  const getDefaultPeriodDates = (periodType) => {
    const today = new Date();
    let start, end;

    switch (periodType) {
      case 'daily':
        start = today.toISOString().split('T')[0];
        end = start;
        break;
      case 'weekly':
        const dayOfWeek = today.getDay();
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - dayOfWeek + 1); // Monday
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6); // Sunday
        start = weekStart.toISOString().split('T')[0];
        end = weekEnd.toISOString().split('T')[0];
        break;
      case 'monthly':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      default:
        start = today.toISOString().split('T')[0];
        end = start;
    }

    return { start, end };
  };

  // Open create modal
  const handleOpenCreateModal = () => {
    const dates = getDefaultPeriodDates('weekly');
    setFormData({
      member_id: members[0]?.id || '',
      period_type: 'weekly',
      period_start: dates.start,
      period_end: dates.end,
      target_meetings: 10,
      target_new_clients: 5,
      target_trials: 2,
      target_sales: 1
    });
    setEditingTarget(null);
    setShowCreateModal(true);
  };

  // Open bulk modal
  const handleOpenBulkModal = () => {
    const dates = getDefaultPeriodDates('weekly');
    setBulkData({
      member_ids: members.map(m => m.id),
      period_type: 'weekly',
      period_start: dates.start,
      period_end: dates.end,
      target_meetings: 10,
      target_new_clients: 5,
      target_trials: 2,
      target_sales: 1
    });
    setShowBulkModal(true);
  };

  // Edit target
  const handleEditTarget = (target) => {
    setFormData({
      member_id: target.member_id,
      period_type: target.period_type,
      period_start: target.period_start.split('T')[0],
      period_end: target.period_end.split('T')[0],
      target_meetings: target.target_meetings,
      target_new_clients: target.target_new_clients,
      target_trials: target.target_trials,
      target_sales: target.target_sales
    });
    setEditingTarget(target);
    setShowCreateModal(true);
  };

  // Save target
  const handleSaveTarget = async () => {
    setSaving(true);
    try {
      if (editingTarget) {
        await fieldTeamApi.updateTarget(editingTarget.id, formData);
      } else {
        await fieldTeamApi.createTarget(formData);
      }
      setShowCreateModal(false);
      setEditingTarget(null);
      fetchTargets();
    } catch (error) {
      console.error('Failed to save target:', error);
      alert(error.response?.data?.error || 'Failed to save target');
    } finally {
      setSaving(false);
    }
  };

  // Save bulk targets
  const handleSaveBulkTargets = async () => {
    setSaving(true);
    try {
      await fieldTeamApi.createBulkTargets(bulkData);
      setShowBulkModal(false);
      fetchTargets();
    } catch (error) {
      console.error('Failed to create bulk targets:', error);
      alert(error.response?.data?.error || 'Failed to create targets');
    } finally {
      setSaving(false);
    }
  };

  // Delete target
  const handleDeleteTarget = async (target) => {
    if (!confirm(`Delete target for ${target.member_name}?`)) return;
    try {
      await fieldTeamApi.deleteTarget(target.id);
      fetchTargets();
    } catch (error) {
      console.error('Failed to delete target:', error);
    }
  };

  // Get progress color
  const getProgressColor = (percentage) => {
    if (percentage >= 100) return '#22c55e';
    if (percentage >= 75) return '#84cc16';
    if (percentage >= 50) return '#eab308';
    if (percentage >= 25) return '#f59e0b';
    return '#ef4444';
  };

  // Get status icon
  const getStatusIcon = (target) => {
    const meetingPct = target.target_meetings > 0
      ? (target.actual_meetings / target.target_meetings) * 100
      : 100;
    const clientsPct = target.target_new_clients > 0
      ? (target.actual_new_clients / target.target_new_clients) * 100
      : 100;

    const avgPct = (meetingPct + clientsPct) / 2;

    if (avgPct >= 100) {
      return <CheckCircle size={16} className="status-icon achieved" />;
    }
    if (avgPct >= 50) {
      return <Clock size={16} className="status-icon in-progress" />;
    }
    return <AlertCircle size={16} className="status-icon behind" />;
  };

  // Format date range
  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);

    if (start === end) {
      return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  // Progress bar component
  const ProgressBar = ({ actual, target, label }) => {
    const percentage = target > 0 ? Math.min((actual / target) * 100, 100) : 100;
    const color = getProgressColor(percentage);

    return (
      <div className="progress-item">
        <div className="progress-header">
          <span className="progress-label">{label}</span>
          <span className="progress-value">{actual} / {target}</span>
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${percentage}%`, backgroundColor: color }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="targets-page">
      {/* Header */}
      <div className="field-team-page-header">
        <h1>Targets</h1>
        <p>Set and track team performance targets</p>
      </div>

      {/* Stats */}
      <div className="stat-blocks">
        <div className="stat-block">
          <div className="stat-icon"><Target size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{pagination.total}</span>
            <span className="stat-label">Total Targets</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">
              {targets.filter(t => {
                const mp = t.target_meetings > 0 ? t.actual_meetings >= t.target_meetings : true;
                const cp = t.target_new_clients > 0 ? t.actual_new_clients >= t.target_new_clients : true;
                return mp && cp;
              }).length}
            </span>
            <span className="stat-label">Achieved</span>
          </div>
        </div>
        <div className="stat-block">
          <div className="stat-icon"><Users size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{members.length}</span>
            <span className="stat-label">Team Members</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="targets-toolbar">
        <div className="toolbar-left">
          <div className="view-toggle">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              List View
            </button>
            <button
              className={viewMode === 'team' ? 'active' : ''}
              onClick={() => setViewMode('team')}
            >
              Team Overview
            </button>
          </div>

          <select
            className="filter-select"
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
          >
            <option value="all">All Periods</option>
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active Only
          </label>
        </div>

        <div className="toolbar-right">
          <button className="btn btn-secondary" onClick={handleOpenBulkModal}>
            <Users size={14} />
            Bulk Create
          </button>
          <button className="btn btn-primary" onClick={handleOpenCreateModal}>
            <Plus size={14} />
            Add Target
          </button>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'list' ? (
        <>
          {/* Targets Table */}
          <div className="targets-table-container">
            <table className="targets-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Member</th>
                  <th>Period</th>
                  <th>Meetings</th>
                  <th>New Clients</th>
                  <th>Trials</th>
                  <th>Sales</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="loading-cell">Loading...</td>
                  </tr>
                ) : targets.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-cell">No targets found</td>
                  </tr>
                ) : (
                  targets.map((target) => (
                    <tr key={target.id}>
                      <td>{getStatusIcon(target)}</td>
                      <td>
                        <div className="member-cell">
                          <span className="member-name">{target.member_name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="period-cell">
                          <span className="period-type">{target.period_type}</span>
                          <span className="period-dates">{formatDateRange(target.period_start, target.period_end)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{target.actual_meetings || 0}</span>
                          <span className="metric-target">/ {target.target_meetings}</span>
                        </div>
                      </td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{target.actual_new_clients || 0}</span>
                          <span className="metric-target">/ {target.target_new_clients}</span>
                        </div>
                      </td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{target.actual_trials || 0}</span>
                          <span className="metric-target">/ {target.target_trials}</span>
                        </div>
                      </td>
                      <td>
                        <div className="metric-cell">
                          <span className="metric-value">{target.actual_sales || 0}</span>
                          <span className="metric-target">/ {target.target_sales}</span>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="icon-btn" onClick={() => handleEditTarget(target)} title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button className="icon-btn danger" onClick={() => handleDeleteTarget(target)} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                <ChevronLeft size={14} />
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                className="pagination-btn"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      ) : (
        /* Team Overview */
        <div className="team-overview-grid">
          {teamOverview.map((member) => (
            <div key={member.member_id} className={`team-card ${member.has_target ? '' : 'no-target'}`}>
              <div className="team-card-header">
                <span className="team-member-name">{member.member_name}</span>
                {member.has_target ? (
                  <span className="target-badge">{member.target.period_type}</span>
                ) : (
                  <span className="no-target-badge">No Target</span>
                )}
              </div>
              {member.has_target && member.actual ? (
                <div className="team-card-body">
                  <ProgressBar
                    actual={member.actual.meetings}
                    target={member.target.target_meetings}
                    label="Meetings"
                  />
                  <ProgressBar
                    actual={member.actual.new_clients}
                    target={member.target.target_new_clients}
                    label="New Clients"
                  />
                </div>
              ) : (
                <div className="team-card-empty">
                  <button className="btn btn-sm" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      member_id: member.member_id
                    }));
                    handleOpenCreateModal();
                  }}>
                    <Plus size={12} /> Set Target
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTarget ? 'Edit Target' : 'Create Target'}</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Team Member *</label>
                  <select
                    value={formData.member_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, member_id: parseInt(e.target.value) }))}
                    disabled={!!editingTarget}
                  >
                    <option value="">Select member</option>
                    {members.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Period Type *</label>
                  <select
                    value={formData.period_type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const dates = getDefaultPeriodDates(type);
                      setFormData(prev => ({
                        ...prev,
                        period_type: type,
                        period_start: dates.start,
                        period_end: dates.end
                      }));
                    }}
                  >
                    {PERIOD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={formData.period_start}
                    onChange={(e) => setFormData(prev => ({ ...prev, period_start: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={formData.period_end}
                    onChange={(e) => setFormData(prev => ({ ...prev, period_end: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Meetings</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.target_meetings}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_meetings: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target New Clients</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.target_new_clients}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_new_clients: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Trials</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.target_trials}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_trials: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Sales</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.target_sales}
                    onChange={(e) => setFormData(prev => ({ ...prev, target_sales: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveTarget} disabled={saving}>
                {saving ? 'Saving...' : (editingTarget ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bulk Create Targets</h2>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Select Members</label>
                  <div className="member-checkbox-list">
                    <label className="select-all">
                      <input
                        type="checkbox"
                        checked={bulkData.member_ids.length === members.length}
                        onChange={(e) => {
                          setBulkData(prev => ({
                            ...prev,
                            member_ids: e.target.checked ? members.map(m => m.id) : []
                          }));
                        }}
                      />
                      Select All ({members.length})
                    </label>
                    {members.map(m => (
                      <label key={m.id}>
                        <input
                          type="checkbox"
                          checked={bulkData.member_ids.includes(m.id)}
                          onChange={(e) => {
                            setBulkData(prev => ({
                              ...prev,
                              member_ids: e.target.checked
                                ? [...prev.member_ids, m.id]
                                : prev.member_ids.filter(id => id !== m.id)
                            }));
                          }}
                        />
                        {m.name}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Period Type *</label>
                  <select
                    value={bulkData.period_type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const dates = getDefaultPeriodDates(type);
                      setBulkData(prev => ({
                        ...prev,
                        period_type: type,
                        period_start: dates.start,
                        period_end: dates.end
                      }));
                    }}
                  >
                    {PERIOD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={bulkData.period_start}
                    onChange={(e) => setBulkData(prev => ({ ...prev, period_start: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={bulkData.period_end}
                    onChange={(e) => setBulkData(prev => ({ ...prev, period_end: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Meetings</label>
                  <input
                    type="number"
                    min="0"
                    value={bulkData.target_meetings}
                    onChange={(e) => setBulkData(prev => ({ ...prev, target_meetings: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target New Clients</label>
                  <input
                    type="number"
                    min="0"
                    value={bulkData.target_new_clients}
                    onChange={(e) => setBulkData(prev => ({ ...prev, target_new_clients: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Trials</label>
                  <input
                    type="number"
                    min="0"
                    value={bulkData.target_trials}
                    onChange={(e) => setBulkData(prev => ({ ...prev, target_trials: parseInt(e.target.value) || 0 }))}
                  />
                </div>

                <div className="form-group">
                  <label>Target Sales</label>
                  <input
                    type="number"
                    min="0"
                    value={bulkData.target_sales}
                    onChange={(e) => setBulkData(prev => ({ ...prev, target_sales: parseInt(e.target.value) || 0 }))}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSaveBulkTargets}
                disabled={saving || bulkData.member_ids.length === 0}
              >
                {saving ? 'Creating...' : `Create for ${bulkData.member_ids.length} Members`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Targets;
